import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import type { DsTableRow, DsTableSort } from '@gde-mit/angular-design-system';

import { UserService } from '@shared/services/user.service';

import { TranslocoService } from '@jsverse/transloco';

import type { UserManagementStatusFilter } from '../constants/user-management-status-filter.constants';
import type { ManagedTeacherUser, UserManagementInviteReminderListItem } from '../models/managed-teacher-user';
import { MembersApiError, MembersApiService } from '../services/api/members.api.service';
import {
  formatManagedTeacherDateTime,
  managedTeacherFullName,
  matchesUserSearch,
} from '../utils/managed-teacher-user.util';
import {
  canDisableUser,
  canResendInvite,
  isActiveInvite,
  isInviteExpired,
  isInviteExpiringSoon,
} from '../utils/user-management-invite.util';
import { sortManagedTeachers } from '../utils/user-management-sort.util';

const rowId = (row: DsTableRow): string => {
  const id = row['id'];
  return typeof id === 'string' ? id : '';
};

// eslint-disable-next-line @angular-eslint/use-injectable-provided-in -- route-scoped store, provided via USER_MANAGEMENT_ROUTE_PROVIDERS
@Injectable({ providedIn: null })
export class UserManagementStore {
  readonly #api = inject(MembersApiService);
  readonly #userService = inject(UserService);
  readonly #transloco = inject(TranslocoService);

  readonly #allUsers = signal<ManagedTeacherUser[]>([]);
  readonly isLoading = signal(false);
  readonly listError = signal(false);
  readonly searchTerm = signal('');
  readonly statusFilter = signal<UserManagementStatusFilter>('all');
  readonly tableSort = signal<DsTableSort[]>([]);
  readonly selection = signal<DsTableRow[]>([]);

  readonly selectedCount = computed(() => this.selection().length);
  readonly stats = computed(() => {
    const users = this.#allUsers();
    const nowMs = Date.now();
    return {
      total: users.length,
      active: users.filter((user) => user.status === 'Active').length,
      activeInvites: users.filter((user) => isActiveInvite(user, nowMs)).length,
      expiredInvites: users.filter((user) => isInviteExpired(user, nowMs)).length,
      expiringSoonInvites: users.filter((user) => isInviteExpiringSoon(user, nowMs)).length,
    };
  });
  readonly expiredInviteListItems = computed<UserManagementInviteReminderListItem[]>(() =>
    this.#inviteReminderListItems(isInviteExpired),
  );
  readonly expiringSoonInviteListItems = computed<UserManagementInviteReminderListItem[]>(() =>
    this.#inviteReminderListItems(isInviteExpiringSoon),
  );

  readonly #activeLang = toSignal(this.#transloco.langChanges$, {
    initialValue: this.#transloco.getActiveLang(),
  });
  readonly filteredUsers = computed(() => {
    const term = this.searchTerm();
    const status = this.statusFilter();
    const activeLang = this.#activeLang();
    return this.#allUsers().filter((user) => {
      if (status !== 'all' && user.status !== status) {
        return false;
      }
      return matchesUserSearch(user, term, activeLang);
    });
  });
  readonly sortedUsers = computed(() =>
    sortManagedTeachers(this.filteredUsers(), this.tableSort(), this.#activeLang()),
  );
  readonly tableRows = computed<DsTableRow[]>(() => {
    const nowMs = Date.now();
    const activeLang = this.#activeLang();
    return this.sortedUsers().map((user) => ({
      id: user.id,
      fullName: managedTeacherFullName(user, activeLang),
      email: user.email,
      invitedAt: user.invitedAt,
      invitedAtLabel: user.invitedAt ? formatManagedTeacherDateTime(user.invitedAt, activeLang) : '',
      lastLoginAt: user.lastLoginAt ?? '',
      lastLoginAtLabel: user.lastLoginAt ? formatManagedTeacherDateTime(user.lastLoginAt, activeLang) : null,
      status: user.status,
      canDisable: canDisableUser(user),
      canResendInvite: canResendInvite(user, nowMs),
    }));
  });
  readonly totalCount = computed(() => this.sortedUsers().length);
  readonly hasActiveListFilters = computed(() => this.searchTerm().trim().length > 0 || this.statusFilter() !== 'all');

  #loadGeneration = 0;

  async load(options?: { silent?: boolean }): Promise<void> {
    const silent = options?.silent === true;
    const generation = ++this.#loadGeneration;

    if (!silent) {
      this.isLoading.set(true);
      this.listError.set(false);
    }

    try {
      const users = await this.#api.listManagedTeachers();

      if (generation !== this.#loadGeneration) {
        return;
      }

      this.#allUsers.set(users);
      this.listError.set(false);
    } catch (error) {
      if (generation !== this.#loadGeneration) {
        return;
      }

      if (error instanceof MembersApiError && error.status === 403) {
        void this.#userService.logout();
        return;
      }

      if (!silent) {
        this.#allUsers.set([]);
        this.listError.set(true);
      }
    } finally {
      if (!silent && generation === this.#loadGeneration) {
        this.isLoading.set(false);
      }
    }
  }

  setSearchTerm(value: string): void {
    this.searchTerm.set(value);
  }

  setStatusFilter(value: UserManagementStatusFilter): void {
    this.statusFilter.set(value);
  }

  setTableSort(sort: DsTableSort[]): void {
    this.tableSort.set(sort);
  }

  setSelection(rows: DsTableRow[]): void {
    this.selection.set(rows);
  }

  clearSelection(): void {
    this.selection.set([]);
  }

  async disableUsers(ids: readonly string[]): Promise<number> {
    const idSet = new Set(ids);
    const eligible = this.#allUsers().filter((user) => idSet.has(user.id) && canDisableUser(user));
    let succeeded = 0;

    for (const user of eligible) {
      try {
        await this.#api.deactivate(user.id);
        succeeded += 1;
      } catch {
        // Continue bulk deactivate; caller compares succeeded vs attempted.
      }
    }

    if (succeeded > 0) {
      await this.load({ silent: true });
      this.clearSelection();
    }

    return succeeded;
  }

  async inviteMember(input: {
    email: string;
    firstName: string;
    lastName: string;
  }): Promise<{ ok: false; error: unknown } | { ok: true }> {
    try {
      await this.#api.create({
        email: input.email,
        firstName: input.firstName,
        lastName: input.lastName,
      });

      if (this.hasActiveListFilters()) {
        this.setSearchTerm('');
        this.setStatusFilter('all');
      }

      await this.load({ silent: true });
      return { ok: true };
    } catch (error) {
      return { ok: false, error };
    }
  }

  async resendInvites(ids: readonly string[]): Promise<number> {
    const idSet = new Set(ids);
    const eligible = this.#allUsers().filter((user) => idSet.has(user.id) && canResendInvite(user));
    let succeeded = 0;

    for (const user of eligible) {
      try {
        await this.#api.resendInvite(user.id);
        succeeded += 1;
      } catch {
        // Continue bulk resend; caller compares succeeded vs attempted.
      }
    }

    if (succeeded > 0) {
      await this.load({ silent: true });
      this.clearSelection();
    }

    return succeeded;
  }

  async disableUser(row: DsTableRow): Promise<number> {
    return this.disableUsers([rowId(row)]);
  }

  async resendInvite(row: DsTableRow): Promise<number> {
    return this.resendInvites([rowId(row)]);
  }

  async resendInviteByUserId(userId: string): Promise<number> {
    return this.resendInvites([userId]);
  }

  async disableSelectedUsers(): Promise<number> {
    return this.disableUsers(this.selection().map((row) => rowId(row)));
  }

  async resendInvitesForSelection(): Promise<number> {
    return this.resendInvites(this.selection().map((row) => rowId(row)));
  }

  #inviteReminderListItems(
    predicate: (user: ManagedTeacherUser, nowMs: number) => boolean,
  ): UserManagementInviteReminderListItem[] {
    const nowMs = Date.now();
    const activeLang = this.#activeLang();
    return this.#allUsers()
      .filter((user) => predicate(user, nowMs))
      .toSorted((left, right) => Date.parse(left.inviteExpiresAt) - Date.parse(right.inviteExpiresAt))
      .map((user) => ({
        id: user.id,
        fullName: managedTeacherFullName(user, activeLang),
        email: user.email,
        inviteExpiresAtLabel: formatManagedTeacherDateTime(user.inviteExpiresAt, activeLang),
        canResendInvite: canResendInvite(user, nowMs),
      }));
  }
}
